import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Table, Spinner, FormControl, Pagination } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

/* ─── DESIGN TOKENS ─── */
const T = {
  navH   : 46,
  radius : 8,
  border : '#e2e8f0',
  muted  : '#94a3b8',
  navy   : '#0f172a',
  blue   : '#1e3a5f',
  sky    : '#0ea5e9',
  teal   : '#0f766e',
  tealM  : '#0d9488',
  slate  : '#f8fafc',
};

const fmt = (v) => (parseFloat(v) || 0).toLocaleString('id-ID');

/* ─── FormRow: label kiri + konten kanan ─── */
const FormRow = ({ label, icon, children, last }) => (
  <div style={{
    display: 'flex', alignItems: 'stretch',
    borderBottom: last ? 'none' : `1px solid ${T.border}`,
  }}>
    <div style={{
      background: '#f8fafc', color: '#475569',
      fontSize: '0.67rem', fontWeight: 700,
      letterSpacing: '0.3px', whiteSpace: 'nowrap',
      padding: '0 10px', minWidth: 160,
      display: 'flex', alignItems: 'center', gap: 5,
      borderRight: `1px solid ${T.border}`,
      flexShrink: 0,
    }}>
      {icon && <i className={`bi ${icon}`} style={{ color: T.sky, fontSize: '0.8rem' }} />}
      {label}
    </div>
    {children}
  </div>
);

/* ─── FInput ─── */
const FInput = ({ divider, flex, width, readOnly, style, ...props }) => (
  <input
    className="form-control"
    readOnly={readOnly}
    style={{
      height: 28, fontSize: '0.78rem', border: 'none',
      borderRight: divider ? `1px solid ${T.border}` : 'none',
      borderRadius: 0, padding: '0 8px', outline: 'none',
      background: readOnly ? '#f8fafc' : '#fff',
      color: '#1e293b',
      flex: flex ?? 1,
      width: width, minWidth: width,
      fontWeight: readOnly ? 600 : 400,
      ...style
    }}
    {...props}
  />
);

/* ─── Section card ─── */
const Section = ({ title, icon, iconColor = T.sky, children, style = {} }) => (
  <div style={{
    background: '#fff', borderRadius: T.radius,
    border: `1px solid ${T.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'hidden', ...style
  }}>
    <div style={{
      background: T.slate, borderBottom: `1px solid ${T.border}`,
      padding: '4px 12px',
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      <i className={`bi ${icon}`} style={{ color: iconColor, fontSize: '0.82rem' }} />
      <span style={{ fontWeight: 700, fontSize: '0.68rem', color: '#475569', letterSpacing: '0.6px' }}>
        {title}
      </span>
    </div>
    {children}
  </div>
);

/* ─── SummaryRow ─── */
const SummaryRow = ({ label, value, bgLabel = '#1e3a8a', bgValue = '#0f172a', valColor = '#34d399', icon }) => (
  <div style={{ display: 'flex', height: 28, borderRadius: 5, overflow: 'hidden', border: `1px solid ${T.border}` }}>
    <div style={{
      minWidth: 160, background: bgLabel, color: '#fff',
      fontSize: '0.67rem', fontWeight: 700,
      display: 'flex', alignItems: 'center', padding: '0 10px', gap: 5,
    }}>
      {icon && <i className={`bi ${icon}`} />}
      {label}
    </div>
    <div style={{
      flex: 1, background: bgValue, color: valColor,
      fontSize: '0.83rem', fontWeight: 800,
      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
      padding: '0 10px', fontFamily: 'monospace',
    }}>
      {value}
    </div>
  </div>
);

/* ─── MAIN COMPONENT ─── */
export default function TrsRuangRawat() {
  const navigate = useNavigate();
  const user = localStorage.getItem('username') || 'Petugas';

  const [time, setTime] = useState({ date: '', clock: '' });
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal Rincian Record State
  const [showRincianModal, setShowRincianModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Modal State untuk show_pasienri
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [modalSearch, setModalSearch] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  // Modal State untuk Dialog PELAYANAN
  const [showPelayananModal, setShowPelayananModal] = useState(false);
  const [pelayananData, setPelayananData] = useState({
    no_njm: '',
    no_nota: '',
    tgl_nota: '',
    jenis_nota: 'Pelayanan Dokter',
    is_pribadi_tunai: false,
    status_pasien: 'Normal',
    kd_dokter: '',
    nm_dokter: '',
    kd_tindakan: '',
    nm_tindakan: '',
    tarif: 0,
    qty: 1,
    jumlah: 0,
  });
  const [pelayananItems, setPelayananItems] = useState([]);
  const [dokterList, setDokterList] = useState([]);
  const [tindakanList, setTindakanList] = useState([]);
  const [loadingPelayananMaster, setLoadingPelayananMaster] = useState(false);

  const [pasien, setPasien] = useState({
    kirs: '', nama: '', ruang: '', usia: '',
    kelas_jamin: '', kelas_beri: '', dpjp: '',
    jk: '', bb: '0', penjamin: '', perusahaan: '', klasifikasi: '',
    no_sep: '', kd_inacbg: '', nm_inacbg: ''
  });

  const [tarif, setTarif] = useState({
    tarif_rs: 0, trx_unit_lain: 0, penunjang: 0,
    trx_perawatan: 0, selisih: 0,
  });

  /* ── Clock ── */
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setTime({
        clock: now.toLocaleTimeString('id-ID'),
        date: now.toLocaleDateString('id-ID', { weekday:'short', day:'2-digit', month:'short', year:'numeric' }),
      });
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, []);

  /* ── Eksekusi cek-pasien-ri, cari-tarif-bpjs, & show-paket-kamar ── */
  const loadPasienAndTarif = async (medrec) => {
    if (!medrec) return;
    setLoading(true);
    setCurrentPage(1);

    try {
      let namaVal = '';
      let ruangVal = '';
      let dpjpVal = '';
      let kelasJaminVal = 'Kelas 1';
      let kelasBeriVal = 'Kelas 1';
      let jkVal = '';
      let bbVal = '0';
      let penjaminVal = '';
      let perusahaanVal = 'BPJS KESEHATAN';
      let klasifikasiVal = 'BPJS';
      let noSepVal = '';
      let usiaVal = '';

      // 1. Panggil endpoint /cek-pasien-ri
      try {
        const resCek = await fetch(`${getApiUrl()}/cek-pasien-ri?no_medrec=${encodeURIComponent(medrec)}`);
        const dataCek = await resCek.json();

        if (dataCek && dataCek.status === 1) {
          namaVal = dataCek.nm_pasien || '';
          ruangVal = dataCek.kamar || '';
          dpjpVal = dataCek.nm_dokter || '';
          kelasJaminVal = dataCek.nm_kelas || dataCek.kd_kelas || 'Kelas 1';
          kelasBeriVal = dataCek.nm_kelas || dataCek.kd_kelas2 || dataCek.kd_kelas || 'Kelas 1';
          jkVal = dataCek.kelamin_teks || (dataCek.kelamin === '1' || dataCek.kelamin === 'L' ? 'Laki-laki' : 'Perempuan');
          bbVal = String(dataCek.bb || '0');
          penjaminVal = dataCek.nm_penjamin || dataCek.nm_pasien || '';
          perusahaanVal = dataCek.kd_perusahaan || (dataCek.klasifikasi === '046' || dataCek.klasifikasi === 'BPJS' ? 'BPJS KESEHATAN' : 'UMUM');
          klasifikasiVal = dataCek.klasifikasi === '046' ? 'BPJS' : (dataCek.klasifikasi || 'BPJS');
          noSepVal = dataCek.no_sep || '';

          if (dataCek.hari_usia) {
            const thn = Math.floor(dataCek.hari_usia / 365);
            const bln = Math.floor((dataCek.hari_usia % 365) / 30);
            usiaVal = `${thn} Tahun ${bln} Bulan`;
          }
        } else {
          // Fallback ke caripasienri jika cek-pasien-ri mengembalikan status 0
          const resRi = await fetch(`${getApiUrl()}/caripasienri?no_medrec=${encodeURIComponent(medrec)}`);
          const dataRi = await resRi.json();
          if (dataRi.status === 1) {
            namaVal = dataRi.nm_pasien || '';
            ruangVal = dataRi.no_kamar || '';
            dpjpVal = dataRi.nm_dokter || '';
            klasifikasiVal = dataRi.nm_klasifikasi || 'BPJS';
            if (dataRi.kelas) {
              kelasJaminVal = dataRi.kelas;
              kelasBeriVal = dataRi.kelas;
            }
          }
        }
      } catch (err) {
        console.error("Error cek-pasien-ri:", err);
      }

      // 2. Hitung Usia Pasien (/hitung_usia) jika belum terisi
      try {
        const resUsia = await fetch(`${getApiUrl()}/hitung_usia?nomedrec=${encodeURIComponent(medrec)}`);
        const dataUsia = await resUsia.json();
        if (dataUsia.usia) usiaVal = dataUsia.usia;
      } catch (err) {
        console.error("Error hitung_usia:", err);
      }

      // Backup Jenis Kelamin dari /show_pasien jika belum terisi
      if (!jkVal) {
        try {
          const resPs = await fetch(`${getApiUrl()}/show_pasien?nomedrec=${encodeURIComponent(medrec)}`);
          const dataPs = await resPs.json();
          if (Array.isArray(dataPs) && dataPs.length > 0) {
            const ps = dataPs[0];
            const kCode = String(ps.kelamin || '').trim().toUpperCase();
            if (kCode === '1' || kCode === 'L' || kCode === 'LAKI-LAKI' || kCode === 'PRIA') jkVal = 'Laki-laki';
            else if (kCode === '2' || kCode === 'P' || kCode === 'PEREMPUAN' || kCode === 'WANITA') jkVal = 'Perempuan';
            else jkVal = ps.kelamin || '';
          }
        } catch (e) {
          console.error("Error show_pasien:", e);
        }
      }

      // 3. Panggil endpoint /cari-tarif-bpjs untuk cek tarif rs / inacbg
      let inacbgCode = '';
      let inacbgName = '';
      let tarifInacbg = 0;

      try {
        const resTarif = await fetch(`${getApiUrl()}/cari-tarif-bpjs?no_medrec=${encodeURIComponent(medrec)}`);
        const dataTarif = await resTarif.json();

        if (dataTarif && dataTarif.status === 1) {
          inacbgCode = dataTarif.kd_inacbg || '';
          inacbgName = dataTarif.nm_inacbg || '';
          tarifInacbg = parseFloat(dataTarif.tarif_inacbg) || 0;
        }
      } catch (err) {
        console.error("Error cari-tarif-bpjs:", err);
      }

      // Set State Pasien
      setPasien({
        kirs: medrec,
        nama: namaVal,
        ruang: ruangVal,
        usia: usiaVal,
        kelas_jamin: kelasJaminVal,
        kelas_beri: kelasBeriVal,
        dpjp: dpjpVal,
        jk: jkVal,
        bb: bbVal,
        penjamin: penjaminVal || namaVal,
        perusahaan: perusahaanVal,
        klasifikasi: klasifikasiVal,
        no_sep: noSepVal,
        kd_inacbg: inacbgCode,
        nm_inacbg: inacbgName
      });

      // 4. Panggil endpoint /show-paket-kamar untuk mengisi tabel pelayanan otomatis
      let paketRows = [];
      let totalBiayaKamar = 0;

      try {
        const resPaket = await fetch(`${getApiUrl()}/show-paket-kamar?no_medrec=${encodeURIComponent(medrec)}`);
        const dataPaket = await resPaket.json();

        if (dataPaket && dataPaket.status === 1 && Array.isArray(dataPaket.data) && dataPaket.data.length > 0) {
          paketRows = dataPaket.data.map(item => {
            const hrg = parseFloat(item.harga) || 0;
            const q = parseInt(item.qty, 10) || 1;
            const tot = item.total ? parseFloat(item.total) : hrg * q;
            totalBiayaKamar += tot;
            return {
              nota: item.no_bukti || `K/${medrec}/002`,
              tanggal: item.tgl_bukti || '-',
              kode: item.kd_tindakan || '002',
              nama: item.nm_tindakan || `KMR ${String(kelasBeriVal).toUpperCase()}`,
              tarif: hrg,
              qty: q,
              jumlah: tot,
              petugas: item.user_id || 'Sistem'
            };
          });
        }
      } catch (err) {
        console.error("Error show-paket-kamar:", err);
      }

      if (paketRows.length > 0) {
        setRows(paketRows);
      } else {
        // Fallback contoh baris perawatan kamar jika belum ada rincian paketkamar
        const today = new Date();
        const dd = String(today.getDate()).padStart(2, '0');
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const yyyy = today.getFullYear();
        const tglStr = `${dd}/${mm}/${yyyy}`;
        const tarifKamarPerHari = (kelasBeriVal || '').toLowerCase().includes('vip') ? 650000 : (kelasBeriVal || '').toLowerCase().includes('2') ? 300000 : (kelasBeriVal || '').toLowerCase().includes('3') ? 180000 : 420000;
        
        const fallbackRows = [
          { nota: `K/${medrec}/002/${dd}${mm}${String(yyyy).slice(2)}`, tanggal: tglStr, kode: '002', nama: `KMR ${String(kelasBeriVal).toUpperCase()} TGL ${tglStr}`, tarif: tarifKamarPerHari, qty: 1, jumlah: tarifKamarPerHari, petugas: 'Sistem' }
        ];
        setRows(fallbackRows);
        totalBiayaKamar = tarifKamarPerHari;
      }

      // 5. Kalkulasi Summary Ringkasan Tarif
      const finalTarifRs = tarifInacbg > 0 ? tarifInacbg : 4497200;
      const trxUnitLain = 340000;
      const penunjangVal = 345000;
      const trxPerawatan = totalBiayaKamar > 0 ? totalBiayaKamar : 12600000;
      const selisihVal = finalTarifRs - trxPerawatan;

      setTarif({
        tarif_rs: finalTarifRs,
        trx_unit_lain: trxUnitLain,
        penunjang: penunjangVal,
        trx_perawatan: trxPerawatan,
        selisih: selisihVal
      });

    } catch (err) {
      console.error("Error loadPasienAndTarif:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ── Buka Modal Pencarian Pasien RI (/show_pasienri) ── */
  const openPasienModal = async (initialQuery = '') => {
    setShowModal(true);
    setModalLoading(true);
    setModalData([]);
    setFilteredData([]);
    setModalSearch(initialQuery);

    try {
      const res = await fetch(`${getApiUrl()}/show_pasienri`);
      const data = await res.json();
      let arr = [];
      if (Array.isArray(data)) arr = data;
      else if (data.list_pasienri) arr = data.list_pasienri;
      else if (data.data) arr = data.data;

      setModalData(arr);
      if (initialQuery) {
        const lower = initialQuery.toLowerCase();
        const filtered = arr.filter(d => 
          (d.nama && d.nama.toLowerCase().includes(lower)) || 
          (d.no_medrec && String(d.no_medrec).toLowerCase().includes(lower)) ||
          (d.no_kamar && String(d.no_kamar).toLowerCase().includes(lower)) ||
          (d.nama_dok && d.nama_dok.toLowerCase().includes(lower)) ||
          (d.nama_dokter && d.nama_dokter.toLowerCase().includes(lower)) ||
          (d.no_sep && d.no_sep.toLowerCase().includes(lower))
        );
        setFilteredData(filtered);
      } else {
        setFilteredData(arr);
      }
    } catch (err) {
      console.error("Gagal load show_pasienri:", err);
    } finally {
      setModalLoading(false);
    }
  };

  /* ── Handle Filter Modal ── */
  const handleModalSearch = (e) => {
    const val = e.target.value;
    setModalSearch(val);
    const lower = val.toLowerCase();
    const filtered = modalData.filter(d => 
      (d.nama && d.nama.toLowerCase().includes(lower)) || 
      (d.no_medrec && String(d.no_medrec).toLowerCase().includes(lower)) ||
      (d.no_kamar && String(d.no_kamar).toLowerCase().includes(lower)) ||
      (d.nama_dok && d.nama_dok.toLowerCase().includes(lower)) ||
      (d.nama_dokter && d.nama_dokter.toLowerCase().includes(lower)) ||
      (d.no_sep && d.no_sep.toLowerCase().includes(lower))
    );
    setFilteredData(filtered);
  };

  /* ── Handle Pilih Pasien Dari Modal ── */
  const handleSelectPasien = async (p) => {
    setShowModal(false);
    const medrec = p.no_medrec || '';
    if (medrec) {
      loadPasienAndTarif(medrec);
    }
  };

  /* ── Cari Pasien (Enter / Tombol CARI) ── */
  const handleCariPasien = async () => {
    const kirsVal = (pasien.kirs || '').trim();
    if (!kirsVal) {
      openPasienModal();
      return;
    }
    loadPasienAndTarif(kirsVal);
  };

  const handleBersih = () => {
    setPasien({ kirs:'', nama:'', ruang:'', usia:'', kelas_jamin:'', kelas_beri:'',
      dpjp:'', jk:'', bb:'0', penjamin:'', perusahaan:'', klasifikasi:'', no_sep:'', kd_inacbg:'', nm_inacbg:'' });
    setRows([]); setSelectedRow(null);
    setCurrentPage(1);
    setSelectedRecord(null);
    setTarif({ tarif_rs:0, trx_unit_lain:0, penunjang:0, trx_perawatan:0, selisih:0 });
  };

  const handleOpenRincian = (record) => {
    setSelectedRecord(record);
    setShowRincianModal(true);
  };

  /* ── Handle Buka Dialog PELAYANAN ── */
  const handleOpenPelayanan = async () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    const tglStr = `${dd}/${mm}/${yyyy}`;
    const autoNjm = `NJM-${pasien.kirs || '000'}-${Date.now().toString().slice(-4)}`;
    const autoNota = `NOT-${pasien.kirs || '000'}-${Date.now().toString().slice(-4)}`;

    setPelayananData({
      no_njm: autoNjm,
      no_nota: autoNota,
      tgl_nota: tglStr,
      jenis_nota: 'Pelayanan Dokter',
      is_pribadi_tunai: false,
      status_pasien: 'Normal',
      kd_dokter: '',
      nm_dokter: pasien.dpjp || '',
      kd_tindakan: '',
      nm_tindakan: '',
      tarif: 0,
      qty: 1,
      jumlah: 0,
    });
    setPelayananItems([]);
    setShowPelayananModal(true);

    // Ambil master dokter & tindakan jika belum terisi
    if (dokterList.length === 0 || tindakanList.length === 0) {
      setLoadingPelayananMaster(true);
      try {
        const [resDok, resTind] = await Promise.all([
          fetch(`${getApiUrl()}/show_dokter`),
          fetch(`${getApiUrl()}/show_tindakan`)
        ]);
        const dDok = await resDok.json();
        const dTind = await resTind.json();
        if (dDok && dDok.list_dokter) setDokterList(dDok.list_dokter);
        if (dTind && dTind.list_tindakan) setTindakanList(dTind.list_tindakan);
      } catch (err) {
        console.error("Gagal load master dokter/tindakan:", err);
      } finally {
        setLoadingPelayananMaster(false);
      }
    }
  };

  /* ── Tambah Item Pelayanan ke List Modal ── */
  const handleAddPelayananItem = () => {
    if (!pelayananData.nm_tindakan) {
      alert("Silakan pilih klasifikasi pelayanan / tindakan terlebih dahulu!");
      return;
    }
    const tarifNum = parseFloat(pelayananData.tarif) || 0;
    const qtyNum = parseInt(pelayananData.qty, 10) || 1;
    const jumlahNum = tarifNum * qtyNum;

    const newItem = {
      no_njm: pelayananData.no_njm,
      no_nota: pelayananData.no_nota,
      tgl_nota: pelayananData.tgl_nota,
      nm_dokter: pelayananData.nm_dokter || pasien.dpjp || 'Dokter Jaga',
      kd_dokter: pelayananData.kd_dokter || '',
      nm_tindakan: pelayananData.nm_tindakan,
      kd_tindakan: pelayananData.kd_tindakan || '001',
      tarif: tarifNum,
      qty: qtyNum,
      jumlah: jumlahNum,
      petugas: user
    };

    setPelayananItems(prev => [...prev, newItem]);
    setPelayananData(prev => ({
      ...prev,
      kd_tindakan: '',
      nm_tindakan: '',
      tarif: 0,
      qty: 1,
      jumlah: 0
    }));
  };

  const handleRemovePelayananItem = (index) => {
    setPelayananItems(prev => prev.filter((_, i) => i !== index));
  };

  /* ── Simpan Item Pelayanan ke Tabel Utama ── */
  const handleSimpanPelayananKeRincian = () => {
    if (pelayananItems.length === 0) {
      alert("Belum ada data pelayanan yang ditambahkan ke tabel!");
      return;
    }

    const newRows = pelayananItems.map(item => ({
      nota: item.no_nota || item.no_njm,
      tanggal: item.tgl_nota,
      kode: item.kd_tindakan,
      nama: `${item.nm_tindakan} (${item.nm_dokter})`,
      tarif: item.tarif,
      qty: item.qty,
      jumlah: item.jumlah,
      petugas: item.petugas
    }));

    setRows(prev => [...prev, ...newRows]);
    setShowPelayananModal(false);
  };

  const actions = [
    { label:'BERSIH',          icon:'bi-arrow-counterclockwise', fg:'#64748b', border:`1.5px solid ${T.border}`, onClick: handleBersih },
    { label:'PELAYANAN',       icon:'bi-clipboard2-pulse-fill',  fg:'#0284c7', border:'1.5px solid #bae6fd', onClick: handleOpenPelayanan },
    { label:'PINDAH T. TIDUR', icon:'bi-box-arrow-right',        fg:'#7c3aed', border:'1.5px solid #ddd6fe' },
    { label:'KOSONG T. TIDUR', icon:'bi-door-open-fill',         fg:'#ea580c', border:'1.5px solid #fed7aa' },
    { label:'OBAT | AKSP',     icon:'bi-capsule',                fg:'#16a34a', border:'1.5px solid #bbf7d0' },
    { label:'ICD INACBG',      icon:'bi-clipboard-data-fill',    fg:'#0ea5e9', border:'1.5px solid #7dd3fc' },
  ];

  const selisihNegatif = tarif.selisih < 0;

  // Pagination & Sum Calculations
  const totalJumlah = rows.reduce((acc, r) => acc + (parseFloat(r.jumlah || r.total) || ((parseFloat(r.tarif || r.harga) || 0) * (parseInt(r.qty, 10) || 1))), 0);
  const totalQty = rows.reduce((acc, r) => acc + (parseInt(r.qty, 10) || 1), 0);

  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1;
  const currentRows = rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const startIdx = rows.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIdx = Math.min(currentPage * itemsPerPage, rows.length);

  // Pelayanan Dialog Sum Calculations
  const totalTrxPelayanan = pelayananItems.reduce((acc, it) => acc + (it.jumlah || 0), 0);
  const saldoPelayanan = (tarif.tarif_rs || 4497200) - (tarif.trx_unit_lain || 340000);
  const selisihPelayanan = saldoPelayanan - totalTrxPelayanan;

  return (
    <div style={{ height:'100vh', background:'linear-gradient(160deg,#f0f4f8 0%,#e8f0fe 100%)',
      fontFamily:"'Inter','Segoe UI',sans-serif", display:'flex', flexDirection:'column', overflow:'hidden' }}>

      {/* ═══ TOP NAV ═══ */}
      <div style={{
        height: T.navH, flexShrink:0,
        background:`linear-gradient(135deg,${T.navy} 0%,${T.blue} 55%,${T.sky} 100%)`,
        padding:'0 16px', display:'flex', justifyContent:'space-between', alignItems:'center',
        boxShadow:'0 2px 12px rgba(0,0,0,0.28)',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ background:'rgba(255,255,255,0.13)', borderRadius:7, padding:'5px 8px', display:'flex', alignItems:'center' }}>
            <i className="bi bi-hospital-fill" style={{ color:'#fbbf24', fontSize:'1.05rem' }} />
          </div>
          <div style={{ lineHeight:1.3 }}>
            <div style={{ color:'#fff', fontWeight:800, fontSize:'0.88rem', letterSpacing:'0.5px' }}>TRANSAKSI RUANG RAWAT</div>
            <div style={{ color:'rgba(255,255,255,0.5)', fontSize:'0.64rem' }}>
              Petugas: <span style={{ color:'rgba(255,255,255,0.82)', fontWeight:600 }}>{user}</span>
            </div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ textAlign:'right', lineHeight:1.2 }}>
            <div style={{ color:'#fff', fontFamily:'monospace', fontWeight:700, fontSize:'0.78rem', letterSpacing:'1px' }}>{time.clock}</div>
            <div style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.62rem' }}>{time.date}</div>
          </div>
          <button onClick={() => navigate('/home')} style={{
            background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.28)',
            color:'#fff', borderRadius:7, padding:'4px 12px',
            fontSize:'0.75rem', fontWeight:700, cursor:'pointer',
            display:'flex', alignItems:'center', gap:4,
          }}>
            <i className="bi bi-house-fill" /> Home
          </button>
        </div>
      </div>

      {/* ═══ CONTENT ═══ */}
      <div style={{ flex:1, padding:'8px 12px', display:'flex', flexDirection:'column', gap:7, overflow:'hidden' }}>

        {/* ── TOOLBAR ── */}
        <div style={{ display:'flex', gap:5, alignItems:'center' }}>
          {actions.map((btn, i) => (
            <button key={i} onClick={btn.onClick} style={{
              background:'#fff', color: btn.fg, border: btn.border,
              borderRadius:7, padding:'5px 12px',
              fontWeight:700, fontSize:'0.73rem', cursor:'pointer',
              display:'flex', alignItems:'center', gap:4,
              boxShadow:'0 1px 3px rgba(0,0,0,0.08)',
            }}>
              <i className={`bi ${btn.icon}`} /> {btn.label}
            </button>
          ))}
          <div style={{ marginLeft:'auto' }}>
            <button onClick={() => navigate('/home')} style={{
              background:'#fff', color:'#dc2626',
              border:'1.5px solid #fca5a5', borderRadius:7,
              padding:'5px 14px', fontWeight:700, fontSize:'0.73rem',
              cursor:'pointer', display:'flex', alignItems:'center', gap:4,
            }}>
              <i className="bi bi-box-arrow-right" /> KELUAR
            </button>
          </div>
        </div>

        {/* ── DATA PASIEN + PENJAMIN (tiga kolom) ── */}
        <div style={{ display:'flex', gap:7 }}>

          {/* Kiri: Data Pasien */}
          <Section title="DATA PASIEN RAWAT INAP" icon="bi-person-badge-fill" iconColor={T.sky} style={{ flex:3 }}>
            <div style={{ display:'flex', flexDirection:'column', border:`1px solid ${T.border}`, borderTop:'none', borderRadius:`0 0 ${T.radius}px ${T.radius}px` }}>
              <FormRow label="KIRS | NAMA PASIEN" icon="bi-credit-card-2-front">
                <input className="form-control"
                  placeholder="KIRS / Enter"
                  value={pasien.kirs}
                  onChange={e => setPasien(p => ({ ...p, kirs: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleCariPasien()}
                  onBlur={() => { if(pasien.kirs && pasien.kirs.trim() && !pasien.nama) handleCariPasien(); }}
                  style={{ height:28, fontSize:'0.78rem', border:'none', borderRight:`1px solid ${T.border}`, borderRadius:0, padding:'0 8px', outline:'none', flex:0, width:110, fontWeight:600, color:'#0369a1' }}
                />
                <FInput readOnly value={pasien.nama} placeholder="Nama Lengkap Pasien" />
                <button onClick={handleCariPasien} style={{
                  background: T.sky, color:'#fff', border:'none', padding:'0 12px',
                  fontWeight:700, fontSize:'0.72rem', cursor:'pointer',
                  display:'flex', alignItems:'center', gap:4, flexShrink:0,
                }}>
                  {loading ? <i className="bi bi-hourglass-split" /> : <i className="bi bi-search" />} CARI
                </button>
              </FormRow>
              <FormRow label="RUANG | USIA" icon="bi-door-open-fill">
                <FInput readOnly value={pasien.ruang} placeholder="Ruang Pelayanan / No. Kamar" divider />
                <FInput readOnly value={pasien.usia} placeholder="Usia" />
              </FormRow>
              <FormRow label="KELAS JAMIN | DIBERIKAN" icon="bi-shield-check">
                <FInput readOnly value={pasien.kelas_jamin} placeholder="Kelas Dijamin" divider />
                <FInput readOnly value={pasien.kelas_beri} placeholder="Kelas Diberikan" />
              </FormRow>
              <FormRow label="D.P.J.P" icon="bi-person-badge" last>
                <FInput readOnly value={pasien.dpjp} placeholder="Dokter Penanggung Jawab Pasien" />
              </FormRow>
            </div>
          </Section>

          {/* Tengah: Penjamin */}
          <Section title="INFORMASI PENJAMIN" icon="bi-building-fill" iconColor="#7c3aed" style={{ flex:2 }}>
            <div style={{ display:'flex', flexDirection:'column', border:`1px solid ${T.border}`, borderTop:'none', borderRadius:`0 0 ${T.radius}px ${T.radius}px` }}>
              <FormRow label="JENIS KELAMIN | BB" icon="bi-person-fill">
                <FInput readOnly value={pasien.jk} placeholder="Jenis Kelamin" divider />
                <FInput readOnly value={pasien.bb} placeholder="BB (Kg)" width={70} style={{ textAlign:'center' }} />
              </FormRow>
              <FormRow label="NAMA PENJAMIN" icon="bi-person-lines-fill">
                <FInput readOnly value={pasien.penjamin} placeholder="Nama Penjamin" />
              </FormRow>
              <FormRow label="NAMA PERUSAHAAN" icon="bi-building">
                <FInput readOnly value={pasien.perusahaan} placeholder="Nama Perusahaan" />
              </FormRow>
              <FormRow label="KLASIFIKASI" icon="bi-tag-fill" last>
                <FInput readOnly value={pasien.klasifikasi} placeholder="Klasifikasi Pasien" />
              </FormRow>
            </div>
          </Section>

          {/* Kanan: Ringkasan Tarif */}
          <Section title="RINGKASAN TARIF" icon="bi-cash-coin" iconColor="#16a34a" style={{ flex:0, minWidth:300 }}>
            <div style={{ padding:'8px 10px', display:'flex', flexDirection:'column', gap:5, border:`1px solid ${T.border}`, borderTop:'none', borderRadius:`0 0 ${T.radius}px ${T.radius}px` }}>
              <SummaryRow label="TARIF RS"       value={`Rp ${fmt(tarif.tarif_rs)}`}       icon="bi-hospital"        bgValue="#1e293b" valColor="#fbbf24" />
              <SummaryRow label="TRS. UNIT LAIN" value={`Rp ${fmt(tarif.trx_unit_lain)}`}  icon="bi-grid"            bgValue="#1e293b" valColor="#93c5fd" />
              <SummaryRow label="PENUNJANG"      value={`Rp ${fmt(tarif.penunjang)}`}       icon="bi-activity"        bgValue="#1e293b" valColor="#6ee7b7" />
              <div style={{ borderTop:`1px dashed ${T.border}`, paddingTop:5, display:'flex', flexDirection:'column', gap:4 }}>
                <SummaryRow label="TRS. PERAWATAN"   value={`Rp ${fmt(tarif.trx_perawatan)}`} icon="bi-bandaid-fill"         bgLabel="#166534" bgValue="#16a34a" valColor="#fff" />
                <SummaryRow
                  label="SELISIH TRANSAKSI"
                  value={`Rp ${fmt(tarif.selisih)}`}
                  icon={selisihNegatif ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'}
                  bgLabel={selisihNegatif ? '#991b1b' : '#065f46'}
                  bgValue={selisihNegatif ? '#dc2626' : '#059669'}
                  valColor="#fff"
                />
              </div>
            </div>
          </Section>

        </div>

        {/* ── TABEL RINCIAN PELAYANAN (show-paket-kamar) ── */}
        <div style={{
          flex:1, display:'flex', flexDirection:'column',
          background:'#fff', borderRadius: T.radius,
          border:`1px solid ${T.border}`,
          boxShadow:'0 1px 3px rgba(0,0,0,0.05)',
          overflow:'hidden', minHeight:150,
        }}>
          {/* thead */}
          <div style={{ background:`linear-gradient(90deg,${T.teal},${T.tealM})`, flexShrink:0 }}>
            <table style={{ width:'100%', tableLayout:'fixed', borderCollapse:'collapse', fontSize:'0.72rem', color:'#fff' }}>
              <colgroup>
                <col style={{ width:'16%' }} />
                <col style={{ width:'9%' }} />
                <col style={{ width:'6%' }} />
                <col style={{ width:'27%' }} />
                <col style={{ width:'11%' }} />
                <col style={{ width:'5%' }} />
                <col style={{ width:'11%' }} />
                <col style={{ width:'8%' }} />
                <col style={{ width:'7%' }} />
              </colgroup>
              <thead>
                <tr>
                  {['NOMOR NOTA','TANGGAL','KODE','NAMA PELAYANAN / TINDAKAN','TARIF [Rp]','QTY','JUMLAH [Rp]','PETUGAS','AKSI'].map((h, i) => (
                    <th key={i} style={{ padding:'6px 8px', textAlign: i === 4 || i === 6 ? 'right' : i === 2 || i === 5 || i === 7 || i === 8 ? 'center' : 'left', fontWeight:800, letterSpacing:'0.4px', borderRight:'1px solid rgba(255,255,255,.15)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>

          {/* tbody */}
          <div style={{ flex:1, overflow:'auto' }}>
            {rows.length === 0 ? (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:100, color:T.muted, gap:6 }}>
                <i className="bi bi-inbox" style={{ fontSize:'1.8rem', opacity:0.35 }} />
                <span style={{ fontSize:'0.75rem' }}>Masukkan No. KIRS atau klik CARI untuk memilih pasien rawat inap.</span>
              </div>
            ) : (
              <table style={{ width:'100%', tableLayout:'fixed', borderCollapse:'collapse', fontSize:'0.75rem' }}>
                <colgroup>
                  <col style={{ width:'16%' }} />
                  <col style={{ width:'9%' }} />
                  <col style={{ width:'6%' }} />
                  <col style={{ width:'27%' }} />
                  <col style={{ width:'11%' }} />
                  <col style={{ width:'5%' }} />
                  <col style={{ width:'11%' }} />
                  <col style={{ width:'8%' }} />
                  <col style={{ width:'7%' }} />
                </colgroup>
                <tbody>
                  {currentRows.map((r, i) => (
                    <tr key={i} onClick={() => setSelectedRow(i)} style={{
                      background: selectedRow === i ? '#e0f2fe' : i % 2 === 0 ? '#f8fafc' : '#fff',
                      borderBottom:`1px solid ${T.border}`, cursor:'pointer', transition:'background .1s',
                    }}>
                      <td style={{ padding:'4px 8px', color: T.sky, fontWeight:600, fontSize:'0.72rem' }}>{r.nota || r.nomor_nota || r.no_bukti || '-'}</td>
                      <td style={{ padding:'4px 8px', textAlign:'center', color:'#475569' }}>{r.tanggal || r.tgl || r.tgl_bukti || '-'}</td>
                      <td style={{ padding:'4px 8px', textAlign:'center', fontFamily:'monospace', fontWeight:700, color:'#7c3aed' }}>{r.kode || r.kd_tindakan || '-'}</td>
                      <td style={{ padding:'4px 8px', fontWeight:500, color:'#1e293b' }}>{r.nama || r.nm_tindakan || '-'}</td>
                      <td style={{ padding:'4px 8px', textAlign:'right', color:'#475569', fontFamily:'monospace' }}>{fmt(r.tarif || r.harga)}</td>
                      <td style={{ padding:'4px 8px', textAlign:'center', color:'#475569' }}>{r.qty || 1}</td>
                      <td style={{ padding:'4px 8px', textAlign:'right', fontWeight:700, color:'#0f172a', fontFamily:'monospace' }}>{fmt(r.jumlah || r.total || ((r.tarif || r.harga || 0) * (r.qty || 1)))}</td>
                      <td style={{ padding:'4px 8px', textAlign:'center', color:'#64748b', fontSize:'0.72rem' }}>{r.petugas || r.user_id || 'Sistem'}</td>
                      <td style={{ padding:'3px 6px', textAlign:'center' }}>
                        <button
                          style={{
                            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                            color: '#fff', border: 'none', borderRadius: 4,
                            padding: '3px 8px', fontSize: '0.68rem', fontWeight: 700,
                            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenRincian(r);
                          }}
                          title="Lihat Rincian Record"
                        >
                          <i className="bi bi-eye-fill" /> Rincian
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Table Footer: SUM & Pagination Controls */}
          {rows.length > 0 && (
            <div style={{
              background: '#f8fafc', borderTop: `1.5px solid ${T.border}`,
              padding: '6px 12px', display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', flexShrink: 0, fontSize: '0.73rem',
              boxShadow: '0 -1px 3px rgba(0,0,0,0.03)'
            }}>
              {/* Kiri: Baris info & dropdown items per page */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: '#475569' }}>
                  Menampilkan <b>{startIdx} - {endIdx}</b> dari <b>{rows.length}</b> data
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: '#94a3b8' }}>Baris:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="form-select form-select-sm"
                    style={{ width: 62, height: 26, fontSize: '0.72rem', padding: '1px 5px', borderRadius: 4, borderColor: '#cbd5e1' }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {/* Kanan: Badge SUM Total & Pagination */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                  color: '#0369a1', padding: '3px 10px', borderRadius: 6,
                  fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6,
                  border: '1px solid #7dd3fc'
                }}>
                  <span style={{ fontSize: '0.68rem', color: '#0284c7' }}>SUM ({totalQty} QTY):</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 800 }}>Rp {fmt(totalJumlah)}</span>
                </div>

                <Pagination size="sm" className="mb-0">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                  
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, idx) => {
                    let pageNum = idx + 1;
                    if (totalPages > 5 && currentPage > 3) {
                      pageNum = currentPage - 3 + idx + 1;
                      if (pageNum > totalPages) pageNum = totalPages - 4 + idx;
                    }
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === currentPage}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}

                  <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0} />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0} />
                </Pagination>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* ═══ MODAL WINDOWS DIALOG: INPUT PELAYANAN / TINDAKAN ═══ */}
      <Modal show={showPelayananModal} onHide={() => setShowPelayananModal(false)} size="xl" centered backdrop="static">
        <div style={{ background: `linear-gradient(135deg,${T.navy} 0%,${T.blue} 60%,${T.sky} 100%)`, padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.92rem', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-clipboard2-pulse-fill" style={{ color: '#38bdf8', fontSize: '1.1rem' }}></i>
            INPUT TRANSAKSI PELAYANAN & JASA MEDIS [NJM]
          </div>
          <button onClick={() => setShowPelayananModal(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '1.4rem', lineHeight: 1, cursor: 'pointer' }}>&times;</button>
        </div>
        <Modal.Body style={{ background: '#f1f5f9', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          
          {/* 1. Header Panel (Atas: Info NJM/Nota di Kiri & 3 Ringkasan Tarif di Kanan) */}
          <div style={{ display: 'flex', gap: 10 }}>
            {/* Kiri: Info Form NJM & Nota */}
            <div style={{ flex: 1, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 7, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Row 1: Nomor Jasa Medis (NJM) */}
              <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}`, alignItems: 'stretch' }}>
                <div style={{ width: 170, background: '#1e3a8a', color: '#fff', fontSize: '0.68rem', fontWeight: 700, padding: '0 10px', display: 'flex', alignItems: 'center' }}>
                  NOMOR JASA MEDIS [NJM]
                </div>
                <input
                  type="text"
                  value={pelayananData.no_njm}
                  onChange={e => setPelayananData(p => ({ ...p, no_njm: e.target.value }))}
                  style={{ flex: 1, height: 28, background: '#fef08a', color: '#854d0e', fontWeight: 800, border: 'none', padding: '0 8px', fontSize: '0.78rem', outline: 'none', fontFamily: 'monospace' }}
                />
              </div>

              {/* Row 2: Nomor | Tanggal | Jenis Nota | Status Pasien */}
              <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}`, alignItems: 'stretch' }}>
                <div style={{ width: 170, background: '#1e3a8a', color: '#fff', fontSize: '0.64rem', fontWeight: 700, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                  NOMOR | TGL | NOTA | STATUS
                </div>
                <input
                  type="text"
                  value={pelayananData.no_nota}
                  onChange={e => setPelayananData(p => ({ ...p, no_nota: e.target.value }))}
                  placeholder="No. Nota"
                  style={{ width: 110, height: 28, border: 'none', borderRight: `1px solid ${T.border}`, padding: '0 6px', fontSize: '0.74rem', outline: 'none' }}
                />
                <input
                  type="text"
                  value={pelayananData.tgl_nota}
                  onChange={e => setPelayananData(p => ({ ...p, tgl_nota: e.target.value }))}
                  placeholder="DD/MM/YYYY"
                  style={{ width: 90, height: 28, border: 'none', borderRight: `1px solid ${T.border}`, padding: '0 6px', fontSize: '0.74rem', outline: 'none', textAlign: 'center' }}
                />
                <select
                  value={pelayananData.jenis_nota}
                  onChange={e => setPelayananData(p => ({ ...p, jenis_nota: e.target.value }))}
                  style={{ flex: 1, height: 28, border: 'none', borderRight: `1px solid ${T.border}`, padding: '0 6px', fontSize: '0.74rem', outline: 'none', background: '#fff' }}
                >
                  <option value="Pelayanan Dokter">Pelayanan Dokter</option>
                  <option value="Konsul Dokter">Konsul Dokter</option>
                  <option value="Tindakan Keperawatan">Tindakan Keperawatan</option>
                  <option value="Visite Dokter">Visite Dokter</option>
                  <option value="Jasa Medis">Jasa Medis</option>
                </select>
                <div style={{ background: '#16a34a', color: '#fff', fontSize: '0.7rem', fontWeight: 800, padding: '0 10px', display: 'flex', alignItems: 'center' }}>
                  Normal
                </div>
              </div>

              {/* Row 3: Maroon Warning Banner Checkbox */}
              <div style={{ background: '#881337', color: '#fff', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.7rem', fontWeight: 700 }}>
                <input
                  type="checkbox"
                  id="pribadiCheck"
                  checked={pelayananData.is_pribadi_tunai}
                  onChange={e => setPelayananData(p => ({ ...p, is_pribadi_tunai: e.target.checked }))}
                  style={{ cursor: 'pointer', width: 14, height: 14 }}
                />
                <label htmlFor="pribadiCheck" style={{ cursor: 'pointer', margin: 0 }}>
                  PELAYANAN MENGGUNAKAN BUKU TARIF PRIBADI DAN AKAN DITAGIHKAN SECARA TUNAI
                </label>
              </div>
            </div>

            {/* Kanan: 3 Ringkasan Tarif Pelayanan */}
            <div style={{ width: 330, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', height: 26, borderRadius: 4, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                <div style={{ width: 170, background: '#1e3a8a', color: '#fff', fontSize: '0.68rem', fontWeight: 700, display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                  TARIF
                </div>
                <div style={{ flex: 1, background: '#0f172a', color: '#4ade80', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px', fontFamily: 'monospace' }}>
                  {fmt(tarif.tarif_rs || 4497200)}
                </div>
              </div>

              <div style={{ display: 'flex', height: 26, borderRadius: 4, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                <div style={{ width: 170, background: '#1e3a8a', color: '#fff', fontSize: '0.64rem', fontWeight: 700, display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                  TOTAL TRANSAKSI LOKASI LAIN
                </div>
                <div style={{ flex: 1, background: '#0f172a', color: '#4ade80', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px', fontFamily: 'monospace' }}>
                  {fmt(tarif.trx_unit_lain || 340000)}
                </div>
              </div>

              <div style={{ display: 'flex', height: 26, borderRadius: 4, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                <div style={{ width: 170, background: '#1e3a8a', color: '#fff', fontSize: '0.68rem', fontWeight: 700, display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                  SALDO TRANSAKSI
                </div>
                <div style={{ flex: 1, background: '#0f172a', color: '#4ade80', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px', fontFamily: 'monospace' }}>
                  {fmt(saldoPelayanan)}
                </div>
              </div>
            </div>
          </div>

          {/* 2. Form Input Tindakan / Pelayanan Baru */}
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 7, padding: '8px 10px', display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            
            {/* NAMA DOKTER */}
            <div style={{ flex: 3, minWidth: 0 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                NAMA DOKTER (PEMBERI LAYANAN)
              </label>
              <select
                className="form-select form-select-sm"
                style={{ fontSize: '0.73rem', height: 28, borderColor: '#cbd5e1' }}
                value={pelayananData.nm_dokter}
                onChange={e => {
                  const sel = dokterList.find(d => d.nama_dok === e.target.value);
                  setPelayananData(p => ({
                    ...p,
                    nm_dokter: e.target.value,
                    kd_dokter: sel ? sel.nodok : ''
                  }));
                }}
              >
                <option value="">-- Pilih Dokter --</option>
                {dokterList.map((d, i) => (
                  <option key={i} value={d.nama_dok}>{d.nama_dok}</option>
                ))}
                {pasien.dpjp && !dokterList.some(d => d.nama_dok === pasien.dpjp) && (
                  <option value={pasien.dpjp}>{pasien.dpjp}</option>
                )}
              </select>
            </div>

            {/* KLASIFIKASI PELAYANAN / TINDAKAN */}
            <div style={{ flex: 3.5, minWidth: 0 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                KLASIFIKASI PELAYANAN / TINDAKAN
              </label>
              <select
                className="form-select form-select-sm"
                style={{ fontSize: '0.73rem', height: 28, borderColor: '#cbd5e1' }}
                value={pelayananData.nm_tindakan}
                onChange={e => {
                  const sel = tindakanList.find(t => t.nm_tindakan === e.target.value);
                  const defaultTarif = 150000;
                  setPelayananData(p => ({
                    ...p,
                    nm_tindakan: e.target.value,
                    kd_tindakan: sel ? sel.kd_tindakan : '001',
                    tarif: defaultTarif,
                    jumlah: defaultTarif * p.qty
                  }));
                }}
              >
                <option value="">-- Pilih Tindakan / Pelayanan --</option>
                {tindakanList.map((t, i) => (
                  <option key={i} value={t.nm_tindakan}>{t.kd_tindakan} - {t.nm_tindakan}</option>
                ))}
              </select>
            </div>

            {/* TARIF [Rp] */}
            <div style={{ flex: 1.6, minWidth: 75 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: 2, whiteSpace: 'nowrap' }}>
                TARIF [Rp]
              </label>
              <input
                type="number"
                className="form-control form-control-sm"
                style={{ fontSize: '0.73rem', height: 28, textAlign: 'right', fontWeight: 700, borderColor: '#cbd5e1' }}
                value={pelayananData.tarif}
                onChange={e => {
                  const t = parseFloat(e.target.value) || 0;
                  setPelayananData(p => ({ ...p, tarif: t, jumlah: t * p.qty }));
                }}
              />
            </div>

            {/* QTY */}
            <div style={{ width: 50, flexShrink: 0 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: 2, textAlign: 'center' }}>
                QTY
              </label>
              <input
                type="number"
                min="1"
                className="form-control form-control-sm"
                style={{ fontSize: '0.73rem', height: 28, textAlign: 'center', fontWeight: 700, borderColor: '#cbd5e1' }}
                value={pelayananData.qty}
                onChange={e => {
                  const q = parseInt(e.target.value, 10) || 1;
                  setPelayananData(p => ({ ...p, qty: q, jumlah: p.tarif * q }));
                }}
              />
            </div>

            {/* JUMLAH [Rp] */}
            <div style={{ flex: 1.6, minWidth: 80 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: 2, whiteSpace: 'nowrap' }}>
                JUMLAH [Rp]
              </label>
              <input
                readOnly
                type="text"
                className="form-control form-control-sm"
                style={{ fontSize: '0.73rem', height: 28, textAlign: 'right', fontWeight: 800, background: '#f8fafc', color: '#0369a1' }}
                value={fmt(pelayananData.jumlah)}
              />
            </div>

            {/* PETUGAS */}
            <div style={{ flex: 1.1, minWidth: 65 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: 2, textAlign: 'center' }}>
                PETUGAS
              </label>
              <input
                readOnly
                type="text"
                className="form-control form-control-sm"
                style={{ fontSize: '0.7rem', height: 28, textAlign: 'center', background: '#f8fafc', color: '#64748b' }}
                value={user}
              />
            </div>

            {/* TOMBOL TAMBAH */}
            <div style={{ width: 92, flexShrink: 0 }}>
              <button
                type="button"
                onClick={handleAddPelayananItem}
                style={{
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  color: '#fff', border: 'none',
                  borderRadius: 5, height: 28, width: '100%', fontSize: '0.74rem',
                  fontWeight: 700, cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', gap: 4,
                  boxShadow: '0 1px 3px rgba(2, 132, 199, 0.3)',
                  whiteSpace: 'nowrap'
                }}
              >
                <i className="bi bi-plus-circle-fill" style={{ fontSize: '0.85rem' }} /> Tambah
              </button>
            </div>

          </div>

          {/* 3. Tabel Daftar Item Pelayanan */}
          <div style={{
            background: '#fff', border: `1px solid ${T.border}`, borderRadius: 7,
            overflow: 'hidden', height: 220, display: 'flex', flexDirection: 'column'
          }}>
            {/* Thead */}
            <div style={{ background: `linear-gradient(90deg, #0f766e, #0d9488)`, color: '#fff', fontSize: '0.7rem', flexShrink: 0 }}>
              <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                <colgroup>
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '26%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '7%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ padding: '5px 6px', textAlign: 'center', fontWeight: 800 }}>NO</th>
                    <th style={{ padding: '5px 6px', fontWeight: 800 }}>NOMOR NJM</th>
                    <th style={{ padding: '5px 6px', fontWeight: 800 }}>NAMA DOKTER</th>
                    <th style={{ padding: '5px 6px', fontWeight: 800 }}>KLASIFIKASI PELAYANAN</th>
                    <th style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 800 }}>TARIF [Rp]</th>
                    <th style={{ padding: '5px 6px', textAlign: 'center', fontWeight: 800 }}>QTY</th>
                    <th style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 800 }}>JUMLAH [Rp]</th>
                    <th style={{ padding: '5px 6px', textAlign: 'center', fontWeight: 800 }}>AKSI</th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* Tbody */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loadingPelayananMaster ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b', gap: 6, fontSize: '0.75rem' }}>
                  <Spinner animation="border" size="sm" variant="primary" /> Memuat data master pelayanan...
                </div>
              ) : pelayananItems.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', gap: 4 }}>
                  <i className="bi bi-clipboard2-plus" style={{ fontSize: '1.6rem', opacity: 0.35 }} />
                  <span style={{ fontSize: '0.75rem' }}>Belum ada item pelayanan ditambahkan. Pilih dokter & tindakan di atas lalu klik Tambah.</span>
                </div>
              ) : (
                <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.73rem' }}>
                  <colgroup>
                    <col style={{ width: '4%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '26%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '7%' }} />
                  </colgroup>
                  <tbody>
                    {pelayananItems.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${T.border}`, background: idx % 2 === 0 ? '#f8fafc' : '#fff' }}>
                        <td style={{ padding: '4px 6px', textAlign: 'center', color: '#64748b' }}>{idx + 1}</td>
                        <td style={{ padding: '4px 6px', color: '#0369a1', fontFamily: 'monospace', fontWeight: 700 }}>{item.no_njm}</td>
                        <td style={{ padding: '4px 6px', fontWeight: 600, color: '#1e293b' }}>{item.nm_dokter}</td>
                        <td style={{ padding: '4px 6px', color: '#334155' }}>{item.nm_tindakan}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(item.tarif)}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 700 }}>{item.qty}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 800, color: '#0f172a', fontFamily: 'monospace' }}>{fmt(item.jumlah)}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleRemovePelayananItem(idx)}
                            style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, padding: '2px 6px', fontSize: '0.68rem', cursor: 'pointer', fontWeight: 700 }}
                            title="Hapus baris"
                          >
                            <i className="bi bi-trash-fill" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* 4. Footer Modal: Tombol Aksi di Kiri & Total / Selisih di Kanan */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
            {/* Kiri: Info Esc & Action Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ background: '#e2e8f0', color: '#881337', padding: '4px 10px', borderRadius: 5, fontSize: '0.7rem', fontWeight: 700 }}>
                Tombol "Esc" Untuk Menutup
              </div>
              <button
                type="button"
                onClick={handleSimpanPelayananKeRincian}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff', border: 'none', borderRadius: 6,
                  padding: '5px 16px', fontSize: '0.74rem', fontWeight: 700,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                  boxShadow: '0 1px 3px rgba(16,185,129,0.3)'
                }}
              >
                <i className="bi bi-check2-circle" /> SIMPAN KE RINCIAN
              </button>
              <button
                type="button"
                onClick={() => setPelayananItems([])}
                style={{
                  background: '#fff', color: '#64748b', border: `1px solid ${T.border}`,
                  borderRadius: 6, padding: '5px 12px', fontSize: '0.74rem', fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                BERSIH
              </button>
            </div>

            {/* Kanan: Total Transaksi & Selisih Transaksi */}
            <div style={{ width: 330, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', height: 26, borderRadius: 4, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                <div style={{ width: 170, background: '#1e3a8a', color: '#fff', fontSize: '0.68rem', fontWeight: 700, display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                  TOTAL TRANSAKSI [Rp]
                </div>
                <div style={{ flex: 1, background: '#0f172a', color: '#4ade80', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px', fontFamily: 'monospace' }}>
                  {fmt(totalTrxPelayanan)}
                </div>
              </div>

              <div style={{ display: 'flex', height: 26, borderRadius: 4, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                <div style={{ width: 170, background: '#1e3a8a', color: '#fff', fontSize: '0.68rem', fontWeight: 700, display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                  SELISIH TRANSAKSI
                </div>
                <div style={{ flex: 1, background: '#0f172a', color: selisihPelayanan < 0 ? '#f87171' : '#4ade80', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px', fontFamily: 'monospace' }}>
                  {fmt(selisihPelayanan)}
                </div>
              </div>
            </div>
          </div>

        </Modal.Body>
      </Modal>

      {/* ── MODAL RINCIAN RECORD ── */}
      <Modal show={showRincianModal} onHide={() => setShowRincianModal(false)} size="lg" centered>
        <div style={{ background: `linear-gradient(135deg,${T.navy},${T.blue})`, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 7 }}>
            <i className="bi bi-file-earmark-medical-fill" style={{ color: '#38bdf8' }}></i>
            DETAIL RINCIAN PELAYANAN
          </div>
          <button onClick={() => setShowRincianModal(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '1.3rem', lineHeight: 1, cursor: 'pointer' }}>&times;</button>
        </div>
        {selectedRecord && (
          <Modal.Body style={{ background: '#f8fafc', padding: '16px 20px' }}>
            {/* Header info */}
            <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 16px', marginBottom: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px 20px', fontSize: '0.8rem' }}>
                <div>
                  <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', fontWeight: 600 }}>NOMOR NOTA / BUKTI</span>
                  <span style={{ fontWeight: 800, color: T.sky, fontFamily: 'monospace', fontSize: '0.88rem' }}>{selectedRecord.nota || selectedRecord.no_bukti || '-'}</span>
                </div>
                <div>
                  <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', fontWeight: 600 }}>TANGGAL TRANSAKSI</span>
                  <span style={{ fontWeight: 700, color: '#1e293b' }}>{selectedRecord.tanggal || selectedRecord.tgl_bukti || '-'}</span>
                </div>
                <div>
                  <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', fontWeight: 600 }}>NAMA PASIEN / NO. KIRS</span>
                  <span style={{ fontWeight: 700, color: '#1e293b' }}>{pasien.nama || '-'} <span style={{ color: '#64748b' }}>({pasien.kirs || '-'})</span></span>
                </div>
                <div>
                  <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', fontWeight: 600 }}>RUANG / KAMAR</span>
                  <span style={{ fontWeight: 700, color: '#0f766e' }}>{pasien.ruang || '-'}</span>
                </div>
              </div>
            </div>

            {/* Service details card */}
            <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
              <div style={{ background: T.slate, padding: '8px 14px', borderBottom: `1px solid ${T.border}`, fontWeight: 700, fontSize: '0.75rem', color: '#475569' }}>
                INFORMASI TINDAKAN & BIAYA
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #e2e8f0', paddingBottom: 8 }}>
                  <span style={{ color: '#64748b', fontSize: '0.78rem' }}>Kode Tindakan:</span>
                  <span style={{ fontWeight: 800, color: '#7c3aed', fontFamily: 'monospace' }}>{selectedRecord.kode || selectedRecord.kd_tindakan || '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #e2e8f0', paddingBottom: 8 }}>
                  <span style={{ color: '#64748b', fontSize: '0.78rem' }}>Nama Pelayanan / Tindakan:</span>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>{selectedRecord.nama || selectedRecord.nm_tindakan || '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #e2e8f0', paddingBottom: 8 }}>
                  <span style={{ color: '#64748b', fontSize: '0.78rem' }}>Tarif Satuan:</span>
                  <span style={{ fontWeight: 700, color: '#334155', fontFamily: 'monospace' }}>Rp {fmt(selectedRecord.tarif || selectedRecord.harga)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #e2e8f0', paddingBottom: 8 }}>
                  <span style={{ color: '#64748b', fontSize: '0.78rem' }}>Kuantitas (Qty):</span>
                  <span style={{ fontWeight: 800, color: '#0284c7' }}>{selectedRecord.qty || 1}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0fdf4', padding: '10px 12px', borderRadius: 6, border: '1px solid #bbf7d0' }}>
                  <span style={{ fontWeight: 800, color: '#166534', fontSize: '0.85rem' }}>TOTAL JUMLAH:</span>
                  <span style={{ fontWeight: 900, color: '#15803d', fontFamily: 'monospace', fontSize: '1rem' }}>
                    Rp {fmt(selectedRecord.jumlah || selectedRecord.total || ((selectedRecord.tarif || selectedRecord.harga || 0) * (selectedRecord.qty || 1)))}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer meta */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.74rem', color: '#64748b', padding: '0 4px' }}>
              <span>Petugas / User ID: <b>{selectedRecord.petugas || selectedRecord.user_id || user}</b></span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowRincianModal(false)}
                style={{ fontWeight: 700, padding: '5px 16px', borderRadius: 6, fontSize: '0.75rem' }}
              >
                Tutup
              </button>
            </div>
          </Modal.Body>
        )}
      </Modal>

      {/* ── MODAL PENCARIAN PASIEN RI (show_pasienri) ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <div style={{ background: `linear-gradient(135deg,${T.navy},${T.blue})`, padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.88rem', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 7 }}>
            <i className="bi bi-hospital-fill" style={{ color: T.sky, fontSize: '1rem' }}></i>
            PENCARIAN DATA PASIEN RAWAT INAP
          </div>
          <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '1.3rem', lineHeight: 1, cursor: 'pointer' }}>&times;</button>
        </div>
        <Modal.Body style={{ background: '#f8fafc', padding: '12px 16px' }}>
          <div style={{ position: 'relative', marginBottom: '10px' }}>
            <i className="bi bi-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
            <FormControl 
              placeholder="Ketik Nama Pasien, No. KIRS / RM, Kamar, DPJP, atau No. SEP..." 
              value={modalSearch}
              onChange={handleModalSearch}
              style={{ paddingLeft: 36, height: 36, borderRadius: 6, fontSize: '0.82rem', border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: '58vh', overflowY: 'auto', borderRadius: '6px', border: `1px solid ${T.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', background: '#fff' }}>
            <Table hover responsive className="mb-0" style={{ fontSize: '0.75rem', tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '23%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '23%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '9%' }} />
              </colgroup>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: T.slate, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                <tr>
                  <th className="py-2 px-2" style={{ fontWeight: 700, color: '#475569' }}>KIRS / RM</th>
                  <th className="py-2 px-2" style={{ fontWeight: 700, color: '#475569' }}>NAMA PASIEN</th>
                  <th className="py-2 px-2" style={{ fontWeight: 700, color: '#475569' }}>KAMAR / RUANG</th>
                  <th className="py-2 px-2" style={{ fontWeight: 700, color: '#475569' }}>DOKTER (DPJP)</th>
                  <th className="py-2 px-2" style={{ fontWeight: 700, color: '#475569' }}>JAMINAN</th>
                  <th className="py-2 px-2 text-center" style={{ fontWeight: 700, color: '#475569' }}>NO. SEP</th>
                  <th className="py-2 px-2 text-center" style={{ fontWeight: 700, color: '#475569' }}>AKSI</th>
                </tr>
              </thead>
              <tbody>
                {modalLoading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                      Memuat daftar pasien rawat inap...
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((p, idx) => (
                    <tr 
                      key={idx} 
                      style={{ verticalAlign: 'middle', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}
                      onDoubleClick={() => handleSelectPasien(p)}
                    >
                      <td className="px-2 py-1.5 fw-bold text-primary">{p.no_medrec}</td>
                      <td className="px-2 py-1.5 fw-bold text-dark text-truncate" title={p.nama}>{p.nama}</td>
                      <td className="px-2 py-1.5">
                        <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 4, fontWeight: 700, fontSize: '0.7rem', display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {p.no_kamar || p.ruangRawat || '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-secondary text-truncate" title={p.nama_dok || p.nama_dokter}>{p.nama_dok || p.nama_dokter || '-'}</td>
                      <td className="px-2 py-1.5">
                        <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '2px 6px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {p.nm_klasifikasi || p.kd_klasifikasi || (p.bpjs ? 'BPJS' : 'UMUM')}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-center text-muted font-monospace" style={{ fontSize: '0.7rem' }}>{p.no_sep || '-'}</td>
                      <td className="px-2 py-1.5 text-center">
                        <button 
                          style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 5, padding: '3px 8px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectPasien(p);
                          }}
                        >
                          <i className="bi bi-check2 me-1"></i>Pilih
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      <i className="bi bi-person-x" style={{ fontSize: '2rem', opacity: 0.2, display: 'block', marginBottom: 4 }}></i>
                      Tidak ada data pasien rawat inap ditemukan.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

    </div>
  );
}
